import { supabaseClient } from '../config/supabase';
import { monitoringService } from './monitoringService';
import { loggingService } from './loggingService';
import { useAuthStore } from '../stores/authStore';
import { useSubscriptionStore } from '../stores/subscriptionStore';

class StripeService {
  async createCheckoutSession(priceId: string) {
    try {
      const { data: { user } } = await supabaseClient.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      monitoringService.trackUserBehavior('checkout_started', {
        userId: user.id,
        priceId,
      });

      const { data, error } = await supabaseClient.functions.invoke('create-checkout-session', {
        body: {
          priceId: priceId,
          userId: user.id // Przekazujemy userId do funkcji
        }
      });

      if (error) {
        throw new Error(`Failed to create checkout session: ${error.message}`);
      }


      if (!data.url) {
        throw new Error('No checkout URL returned');
      }

      return {
        url: data.url,
        sessionId: data.sessionId
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create checkout session';
      console.error('Error creating checkout session:', errorMessage);
      loggingService.error('Checkout session creation failed', error);
      monitoringService.trackError(error as Error, {
        action: 'createCheckoutSession',
        additionalData: { priceId } // Usunięto userId z catch, bo może nie być zdefiniowane, jeśli błąd wystąpi wcześniej
      });
      throw new Error(errorMessage);
    }
  }

  async createPortalSession(customerId: string) {
    try {
      const { data: { user } } = await supabaseClient.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      monitoringService.trackUserBehavior('portal_session_started', { userId: user.id });

      const { data, error } = await supabaseClient.functions.invoke('create-portal-session', {
        body: {
          customerId: customerId,
          returnUrl: `${window.location.origin}/dashboard`
        }
      });

      if (error) {
        throw new Error(`Failed to create portal session: ${error.message}`);
      }

      if (!data.url) {
        throw new Error('No portal URL returned');
      }

      return data;
    } catch (error) {
      const err = error as Error;
      console.error('Error creating portal session:', err.message);
      loggingService.error('Portal session creation failed', err);
      monitoringService.trackError(err, {
        action: 'createPortalSession',
        additionalData: { customerId }
      });
      throw err;
    }
  }

  async handleSubscriptionChange(sessionId: string) {
    try {
      monitoringService.trackUserBehavior('subscription_change_started', { sessionId });

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-webhook`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${(await supabaseClient.auth.getSession()).data.session?.access_token}`
          },
          body: JSON.stringify({ sessionId })
        }
      );

      const data = await response.json();
      if (!response.ok || data.error) {
        throw new Error(data.error || 'Failed to handle subscription change');
      }
      return data;
    } catch (error) {
      console.error('Error handling subscription change:', error);
      loggingService.error('Subscription change failed', error);
      monitoringService.trackError(error as Error, {
        action: 'handleSubscriptionChange',
        additionalData: { sessionId }
      });
      throw error;
    }
  }

  async handleCheckoutSuccess(sessionId: string) {
    try {
      const { user } = useAuthStore.getState();
      if (!user) throw new Error('No user found');

      monitoringService.trackUserBehavior('checkout_completed', {
        userId: user.id,
        sessionId
      });

      const { data, error } = await supabaseClient.functions.invoke('handle-subscription-change', {
        body: { sessionId }
      });

      if (error) throw error;

      // Refresh subscription status
      await useSubscriptionStore.getState().fetchSubscriptionStatus(user.id);

      return data;
    } catch (error) {
      console.error('Error handling checkout success:', error);
      loggingService.error('Checkout success handling failed', error);
      monitoringService.trackError(error as Error, {
        action: 'handleCheckoutSuccess',
        additionalData: { sessionId }
      });
      throw error;
    }
  }

  async checkSubscriptionStatus(userId: string) {
    try {
      const { data: subscription, error } = await supabaseClient
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active')
        .maybeSingle();

      if (error) throw error;

      if (subscription) {
        const now = new Date();
        const endDate = new Date(subscription.current_period_end);
        const daysLeft = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        if (daysLeft <= 7) {
          monitoringService.trackUserBehavior('subscription_expiring_soon', {
            userId,
            daysLeft
          });
        }
      }

      return subscription;
    } catch (error) {
      loggingService.error('Subscription status check failed', error);
      monitoringService.trackError(error as Error, {
        action: 'checkSubscriptionStatus',
        additionalData: { userId }
      });
      throw error;
    }
  }

  async cancelSubscription(userId: string) {
    try {
      monitoringService.trackUserBehavior('subscription_cancellation_started', { userId });

      const { data: subscription } = await supabaseClient
        .from('subscriptions')
        .select('stripe_subscription_id')
        .eq('user_id', userId)
        .single();

      if (!subscription?.stripe_subscription_id) {
        throw new Error('No active subscription found');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cancel-subscription`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${(await supabaseClient.auth.getSession()).data.session?.access_token}`
          },
          body: JSON.stringify({
            subscriptionId: subscription.stripe_subscription_id
          })
        }
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);

      monitoringService.trackUserBehavior('subscription_cancelled', { userId });
      return data;
    } catch (error) {
      loggingService.error('Subscription cancellation failed', error);
      monitoringService.trackError(error as Error, {
        action: 'cancelSubscription',
        additionalData: { userId }
      });
      throw error;
    }
  }

  // Nowa metoda do tworzenia sesji płatności za kursy
  async createCourseCheckoutSession(
    userId: string,
    courseId: string,
    courseName: string,
    priceInCents: number, // Cena w groszach
    imageUrl?: string
  ) {
    try {
      monitoringService.trackUserBehavior('course_checkout_started', {
        userId,
        courseId,
        priceInCents
      });

      const session = await supabaseClient.auth.getSession();
      if (!session.data.session?.access_token) {
        throw new Error('No auth session found');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-course-checkout-session`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.data.session.access_token}`
          },
          body: JSON.stringify({
            userId,
            courseId,
            courseName,
            imageUrl,
            // Zmieniony successUrl - kieruje do strony MOJEGO kursu z parametrem sukcesu
            successUrl: `${window.location.origin}/moje-kursy/${courseId}?purchase_status=success`,
            cancelUrl: `${window.location.origin}/kursy/${courseId}` // cancelUrl pozostaje bez zmian
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create course checkout session');
      }
      const data = await response.json();

      if (!data.url) {
        throw new Error('No checkout URL returned');
      }

      return {
        url: data.url,
        sessionId: data.sessionId
      };
    } catch (error) {
      console.error('Error creating course checkout session:', error);
      loggingService.error('Course checkout session creation failed', error);
      monitoringService.trackError(error as Error, {
        action: 'createCourseCheckoutSession',
        additionalData: { userId, courseId, priceInCents }
      });

      // W przypadku błędu nie przekierowujemy do testowego linku w produkcji
      throw new Error('Nie udało się utworzyć sesji płatności. Spróbuj ponownie później.');
    }
  }
}

export const stripeService = new StripeService();
